// Claims submission settings for one carrier (Settings > Integrations >
// Carriers > gear icon next to "Automatic claims submissions").
//
// Edits the email (from / to / cc / reply-to / subject / body) and the
// schedule (daily / weekly / monthly at HH:MM UK) that carrier-claims-jobs
// runs on, shows what is waiting and what last went out, and offers a
// "Send now" for flushing the pending batch outside the schedule.
//
// The form is a local draft saved as one PATCH — a half-typed address must
// never reach the sender, and the BE validates the whole config together.
import { useEffect, useState } from "react";
import { Download, Loader2, Send, X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../../../i18n";
import { apiFetch } from "../../../api/apiFetch";
import { useReadOnly } from "../../../access/ReadOnly";
import type {
    CarrierClaimsConfig, CarrierClaimsFrequency, CarrierClaimsStatus, CarrierConfig,
} from "./types";

interface Props {
    carrier: CarrierConfig;
    onClose: () => void;
    onUpdated: (next: CarrierConfig) => void;
}

const WEEKDAY_KEYS = ["claims_day_0", "claims_day_1", "claims_day_2", "claims_day_3", "claims_day_4", "claims_day_5", "claims_day_6"];

const formatTs = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
};

const CarrierClaimsSettingsModal = ({ carrier, onClose, onUpdated }: Props) => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [draft, setDraft] = useState<CarrierClaimsConfig>({ ...carrier.claimsConfig });
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<CarrierClaimsStatus | null>(null);

    const loadStatus = async () => {
        try {
            const res = await apiFetch(`/admin/carriers/${carrier.id}/claims/status`, {});
            if (!res.ok) return;
            const json = await res.json();
            setStatus(json.status ?? null);
        } catch { /* status is informational */ }
    };

    useEffect(() => { void loadStatus(); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carrier.id]);

    const set = <K extends keyof CarrierClaimsConfig>(key: K, value: CarrierClaimsConfig[K]) =>
        setDraft(prev => ({ ...prev, [key]: value }));

    const dirty = JSON.stringify(draft) !== JSON.stringify(carrier.claimsConfig);

    const save = async () => {
        if (readOnly) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiFetch(`/admin/carriers/${carrier.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ claimsConfig: draft }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
            onUpdated(json.carrier as CarrierConfig);
            toast.success(t("claims_settings_saved"));
            void loadStatus();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    };

    const sendNow = async () => {
        if (readOnly) return;
        const n = status?.pendingCount ?? 0;
        if (n === 0) return;
        if (!window.confirm(t("claims_settings_send_now_confirm", { n: String(n), to: carrier.claimsConfig.toAddress }))) return;
        setSending(true);
        setError(null);
        try {
            const res = await apiFetch(`/admin/carriers/${carrier.id}/claims/send-now`, { method: "POST" });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
            toast.success(json?.message || t("saved"));
            await loadStatus();
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSending(false);
        }
    };

    // `file` = "sheet" → the XLSX that was emailed; "invoices" → the supplier
    // invoices PDF sent with it (only offered when that submission had one).
    const downloadLast = async (file: "sheet" | "invoices" = "sheet") => {
        const sub = status?.lastSubmission;
        if (!sub) return;
        try {
            const qs = file === "invoices" ? "?file=invoices" : "";
            const res = await apiFetch(`/admin/carriers/claims/submissions/${encodeURIComponent(sub.submission_id)}/download${qs}`, {});
            if (!res.ok) { toast.error(t("server_error")); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = file === "invoices" ? (sub.invoices_filename || "supplier-invoices.pdf") : (sub.filename || "claims.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t("server_error"));
        }
    };

    const busy = saving || sending;
    // Cancel/close stay enabled in read-only so a leader can still dismiss
    // the modal; every field and mutating action folds readOnly in here.
    const fieldsDisabled = busy || readOnly;

    return (
        <div className="modal-overlay" onClick={() => { if (!busy) onClose(); }}>
            <div className="modal-card carrier-claims-card" onClick={e => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("claims_settings_title")}</h2>
                    <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label={t("close")}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body carrier-claims-body">
                    {/* Status strip */}
                    <div className="carrier-claims-status">
                        <div>
                            <span className="carrier-claims-status-label">{t("claims_settings_status_pending")}</span>
                            <span className="carrier-claims-status-value">{status ? status.pendingCount : "…"}</span>
                        </div>
                        <div>
                            <span className="carrier-claims-status-label">{t("claims_settings_status_next")}</span>
                            <span className="carrier-claims-status-value">
                                {status
                                    ? (status.enabled ? (formatTs(status.nextDueAt) ?? "—") : t("claims_settings_off"))
                                    : "…"}
                            </span>
                        </div>
                        <div>
                            <span className="carrier-claims-status-label">{t("claims_settings_status_last_run")}</span>
                            <span className="carrier-claims-status-value">{status ? (formatTs(status.lastRunAt) ?? t("claims_settings_never")) : "…"}</span>
                        </div>
                        <div>
                            <span className="carrier-claims-status-label">{t("claims_settings_last_submission")}</span>
                            <span className="carrier-claims-status-value">
                                {!status ? "…" : !status.lastSubmission ? t("claims_settings_none") : (
                                    <>
                                        {formatTs(status.lastSubmission.created_at)} · {status.lastSubmission.claim_count}
                                        {status.lastSubmission.send_error && (
                                            <span className="admin-integrations-field-error carrier-claims-send-error">
                                                {t("claims_settings_send_failed", { error: status.lastSubmission.send_error })}
                                            </span>
                                        )}
                                        <button type="button" className="carrier-claims-link" onClick={() => void downloadLast()}>
                                            <Download size={12} /> {t("claims_settings_download")}
                                        </button>
                                        {status.lastSubmission.invoices_filename && (
                                            <button type="button" className="carrier-claims-link" onClick={() => void downloadLast("invoices")}>
                                                <Download size={12} /> {t("claims_settings_download_invoices")}
                                            </button>
                                        )}
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                    {status && status.mailConfigured && status.hasRecipient === false && (
                        <p className="admin-integrations-field-error">{t("claims_settings_no_recipient")}</p>
                    )}
                    {status && !status.mailConfigured && (
                        <p className="admin-integrations-field-error">{t("claims_settings_mail_not_configured")}</p>
                    )}
                    {status && !status.enabled && (
                        <p className="admin-integrations-field-hint">{t("claims_settings_disabled_note")}</p>
                    )}

                    {/* Email */}
                    <div className="carrier-claims-grid">
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_from")}</span>
                            <input type="email" value={draft.fromAddress} disabled={fieldsDisabled}
                                onChange={e => set("fromAddress", e.target.value)} />
                        </label>
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_from_name")}</span>
                            <input type="text" value={draft.fromName} disabled={fieldsDisabled}
                                onChange={e => set("fromName", e.target.value)} />
                        </label>
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_to")}</span>
                            <input type="text" value={draft.toAddress} disabled={fieldsDisabled}
                                onChange={e => set("toAddress", e.target.value)} />
                        </label>
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_cc")}</span>
                            <input type="text" value={draft.ccAddress} disabled={fieldsDisabled}
                                onChange={e => set("ccAddress", e.target.value)} />
                        </label>
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_reply_to")}</span>
                            <input type="email" value={draft.replyToAddress} disabled={fieldsDisabled}
                                onChange={e => set("replyToAddress", e.target.value)} />
                        </label>
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_subject")}</span>
                            <input type="text" value={draft.subject} disabled={fieldsDisabled}
                                onChange={e => set("subject", e.target.value)} />
                        </label>
                    </div>
                    <label className="admin-integrations-field">
                        <span className="admin-integrations-field-label">{t("claims_settings_body")}</span>
                        <textarea rows={6} value={draft.bodyText} disabled={fieldsDisabled}
                            onChange={e => set("bodyText", e.target.value)} />
                    </label>
                    <p className="admin-integrations-field-hint">{t("claims_settings_placeholders")}</p>

                    {/* Schedule */}
                    <div className="carrier-claims-grid carrier-claims-grid--schedule">
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_frequency")}</span>
                            <select value={draft.frequency} disabled={fieldsDisabled}
                                onChange={e => set("frequency", e.target.value as CarrierClaimsFrequency)}>
                                <option value="daily">{t("claims_freq_daily")}</option>
                                <option value="weekly">{t("claims_freq_weekly")}</option>
                                <option value="monthly">{t("claims_freq_monthly")}</option>
                            </select>
                        </label>
                        {draft.frequency === "weekly" && (
                            <label className="admin-integrations-field">
                                <span className="admin-integrations-field-label">{t("claims_settings_weekly_day")}</span>
                                <select value={draft.weeklyDay} disabled={fieldsDisabled}
                                    onChange={e => set("weeklyDay", Number(e.target.value))}>
                                    {[1, 2, 3, 4, 5, 6, 0].map(d => (
                                        <option key={d} value={d}>{t(WEEKDAY_KEYS[d])}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {draft.frequency === "monthly" && (
                            <label className="admin-integrations-field">
                                <span className="admin-integrations-field-label">{t("claims_settings_monthly_day")}</span>
                                <select value={draft.monthlyDay} disabled={fieldsDisabled}
                                    onChange={e => set("monthlyDay", Number(e.target.value))}>
                                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="admin-integrations-field">
                            <span className="admin-integrations-field-label">{t("claims_settings_time")}</span>
                            <input type="time" value={draft.sendTime} disabled={fieldsDisabled}
                                onChange={e => set("sendTime", e.target.value)} />
                        </label>
                    </div>
                    <p className="admin-integrations-field-hint">{t("claims_settings_time_hint")}</p>

                    {error && <p className="admin-integrations-field-error">{error}</p>}

                    <div className="modal-confirm-buttons carrier-claims-actions">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={fieldsDisabled || !status || status.pendingCount === 0 || !status.mailConfigured || status.hasRecipient === false}
                            onClick={() => void sendNow()}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                            {t("claims_settings_send_now")}
                        </button>
                        <span className="carrier-claims-actions-spacer" />
                        <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
                            {t("cancel")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => void save()}
                            disabled={fieldsDisabled || !dirty}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {saving ? <Loader2 size={14} className="spin" /> : null}
                            {t("save")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CarrierClaimsSettingsModal;
